import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { TERMS_URL } from "../config";
import { HeroLeadForm } from "./HeroLeadForm";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BENEFITS = [
  "Free, no-obligation review",
  "No statement needed to get started",
  "Help with setup and transition if you choose to switch",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden pb-16 pt-28 lg:pt-32">
      <motion.video
        autoPlay
        muted
        loop
        playsInline
        data-testid="hero-video"
        poster="https://images.pexels.com/videos/6683948/pexels-photo-6683948.jpeg?auto=compress&cs=tinysrgb&w=1600"
        style={{ y: bgY }}
        className="absolute inset-0 h-[125%] w-full object-cover"
      >
        <source src="/videos/hero.mp4" type="video/mp4" />
      </motion.video>
      <div className="absolute inset-0 bg-[#081426]/75" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-mint"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-mint" />
            For businesses that accept credit cards
          </motion.p>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-[#FFFFFF] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.2}>Could Your Business</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="text-mint">Pay Less</span> in Credit Card
            </MaskedLine>
            <MaskedLine delay={0.5}>Processing Fees?</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#FFFFFF]/80 sm:text-lg"
          >
            Get a free review of your current processing costs. We'll help you understand what
            you're paying, identify potential savings, and explain what switching would involve.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-7 space-y-3"
          >
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm text-[#FFFFFF]/85 sm:text-base">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mint/15">
                  <Check size={14} className="text-mint" />
                </span>
                {b}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="mt-8 rounded-2xl border border-[#FFFFFF]/10 bg-[#FFFFFF]/5 p-5 backdrop-blur-sm"
          >
            <p className="text-sm leading-relaxed text-[#FFFFFF]/75">
              "We are so glad we made the switch to CardConnect... it has saved us money in monthly
              credit card fees."
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mint">
              — Living Well Chiropractic, CardConnect customer
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-5 text-xs leading-relaxed text-[#FFFFFF]/50"
          >
            Eligible businesses may qualify for up to $1,000 in switching credit.{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-terms-link"
              className="text-amber-400 underline">
              Terms and conditions apply.
            </a>
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
        >
          <HeroLeadForm />
        </motion.div>
      </div>
    </section>
  );
};
